import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { uniqBy } from 'lodash-es'
import { action, observable, computed, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Subject, switchMap } from 'rxjs'
import type { FetchMemberCouponsFn, ListCouponsFn } from '@/features/billing'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { type UserCoupon } from '@/features/billing/coupons/UserCoupon'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { formatMoney } from '@/utils/money-util'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * Creates the view model for the Apply Coupon as Admin dialog of a job.
 */
export type ApplyCouponAsAdminDialogViewModelFactory = (
  job: Job,
) => ApplyCouponAsAdminDialogViewModel

/**
 * View Model for the Apply Coupon as Admin dialog.
 */
export class ApplyCouponAsAdminDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _triggerSearch = new Subject<string>()

  @observable private accessor _memberCoupons: UserCoupon[] = []

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Coupon to add.
   */
  @observable accessor selectedCouponId: Id = Id.none

  /**
   * SearchTerm to filter coupons by.
   */
  @observable accessor searchTerm = ''

  /**
   * Pages through the workspace's coupons.
   */
  readonly scrollProvider = new InfiniteScrollProvider({
    limit: 20,
    fetchItems: ({ paging, signal }) =>
      this._createAlertRetryWrapper(
        'Loading coupons failed',
        signal,
      )(() =>
        this._listCoupons(
          {
            ...paging,
            couponType: 'Regular',
            searchText: this.searchTerm,
            status: 'Active',
            workspaceId: this._job.workspace.id,
          },
          signal,
        ),
      ),
  })

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _fetchMemberCoupons: FetchMemberCouponsFn,
    private readonly _listCoupons: ListCouponsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(
      this.modal.show$.pipe(
        switchMap(() =>
          this._triggerSearch.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            takeUntil(this.modal.close$),
          ),
        ),
        switchMap(() =>
          withAbortSignal((signal) =>
            this.scrollProvider
              .fetch({ signal, staleWhileRevalidate: true })
              .catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * The workspace's regular coupons the owner can still redeem, filtered by the search term.
   * Coupons the owner already holds come first, even when they're no longer
   * active.
   */
  @computed
  get coupons() {
    const redeemedCoupons = this._memberCoupons
      .filter((memberCoupon) => memberCoupon.redeemed)
      .map((memberCoupon) => memberCoupon.coupon.id)

    const heldCoupons = this._memberCoupons
      .map((memberCoupon) => memberCoupon.coupon)
      .filter((coupon) => coupon.workspaceId === this._job.workspace.id)

    const loadedCoupons = uniqBy(
      [...heldCoupons, ...this.scrollProvider.items],
      (coupon) => coupon.id,
    )

    const availableCoupons = loadedCoupons.filter(
      (coupon) =>
        coupon.couponType.type === 'Regular' &&
        (coupon.duration === 'Forever' || !redeemedCoupons.includes(coupon.id)),
    )

    if (!this.searchTerm) {
      return availableCoupons
    }

    const searchTerm = this.searchTerm.toLowerCase()
    return availableCoupons.filter(
      (coupon) =>
        coupon.code.toLowerCase().includes(searchTerm) ||
        coupon.friendlyName?.toLowerCase().includes(searchTerm),
    )
  }

  /**
   * Shows the Apply Coupon as Admin Dialog.
   */
  @action.bound
  show() {
    const { signal } = this._lifecycle
    this._createAlertRetryWrapper(
      'Loading redeemed coupons failed',
      signal,
    )(() => this._fetchMemberCoupons(this._job.owner, signal))
      .then((memberCoupons) =>
        runInAction(() => {
          this._memberCoupons = memberCoupons
        }),
      )
      .catch(() => undefined)
    this.scrollProvider
      .fetch({ signal, staleWhileRevalidate: true })
      .catch(() => undefined)
    this.modal.show()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets couponId to add.
   */
  @action.bound
  setCoupon(coupon: Coupon) {
    this.selectedCouponId = coupon.id
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.selectedCouponId = Id.none

    this.applyCouponAsAdmin.reset()
  }

  /**
   * The coupon's discount, as its list item shows it.
   */
  couponDiscount({ offType }: Coupon) {
    return offType.type === 'AmountOff'
      ? `${formatMoney(offType.amountOff)} off`
      : `${offType.percentOff}% off`
  }

  /**
   * The most the coupon saves, or `null` when it is uncapped.
   */
  couponMaxSavings({ maxOff }: Coupon) {
    if (maxOff?.type === 'MaxAmountOff') {
      return maxOff.amountOff ? formatMoney(maxOff.amountOff) : null
    }

    return maxOff?.percentOff ? `${maxOff.percentOff}%` : null
  }

  /**
   * Narrows the coupons by code or name.
   */
  @action.bound
  searchCoupon(searchTerm: string) {
    this.searchTerm = searchTerm
    this._triggerSearch.next(searchTerm)
  }

  /**
   * Applies the coupon as Admin.
   */
  applyCouponAsAdmin = task.resolved(async () => {
    if (!this.selectedCouponId) {
      return
    }

    await this._jobStore
      .applyCouponAsAdmin(this._job, this.selectedCouponId)
      .catch(this._createAlertErrorHandler('Applying the coupon failed'))

    this.modal.close()
    this._newAlert()
      .saying('Coupon applied!', `Coupon was applied to ${this._job.name}.`)
      .withState('success')
      .autoDismiss()
      .show()
  })
}
